import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { seo, breadcrumbScript } from "@/lib/seo";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { TOOLKIT_TOOLS, categoryOf } from "@/lib/toolkit/registry";

export const Route = createFileRoute("/toolkit/c/$categoryId")({
  loader: ({ params }) => {
    const category = categoryOf(params.categoryId);
    if (!category) throw notFound();
    return { name: category.name, description: category.description };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Toolkit category" }, { name: "robots", content: "noindex" }] };
    }
    return {
      ...seo({
        title: `${loaderData.name} Tools — JEE Toolkit | The Quantum Minds`,
        description: loaderData.description,
        path: `/toolkit/c/${params.categoryId}`,
      }),
      scripts: [
        breadcrumbScript([
          { name: "Home", path: "/" },
          { name: "JEE Toolkit", path: "/toolkit" },
          { name: loaderData.name, path: `/toolkit/c/${params.categoryId}` },
        ]),
      ],
    };
  },
  component: CategoryPage,
  notFoundComponent: CategoryNotFound,
});

function CategoryPage() {
  const { categoryId } = Route.useParams();
  const data = Route.useLoaderData();
  const tools = TOOLKIT_TOOLS.filter((t) => t.category === categoryId);
  const bio = categoryId === "biology";

  return (
    <main className="relative page-shell min-w-0 overflow-x-clip px-5 lg:px-8 pt-8 [box-sizing:border-box]">
      {bio ? <div aria-hidden className="toolkit-bio-ambient" /> : null}
      <Link
        to="/toolkit"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors duration-200 hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" /> Toolkit
      </Link>
      <h1 className="mt-3 text-2xl font-bold tracking-tight">{data.name}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{data.description}</p>

      <ul className="mt-5 w-full min-w-0 max-w-full space-y-2 pb-8">
        {tools.map((tool) => (
          <li key={tool.id} className="min-w-0 max-w-full">
            <Link
              to="/toolkit/$toolId"
              params={{ toolId: tool.id }}
              className={`glass flex min-w-0 max-w-full items-center gap-3 rounded-3xl p-4 [box-sizing:border-box] transition-transform duration-200 active:scale-[0.99] ${
                bio ? "toolkit-bio-card" : ""
              }`}
            >
              <tool.icon className="size-5 shrink-0 text-primary" />
              <span className="min-w-0 flex-1">
                <span className="block break-words text-sm font-semibold">{tool.name}</span>
                <span className="block break-words text-xs leading-snug text-muted-foreground">
                  {tool.description}
                </span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}

function CategoryNotFound() {
  return (
    <main className="page-shell min-w-0 overflow-x-clip px-5 lg:px-8 pt-16 text-center [box-sizing:border-box]">
      <h1 className="text-xl font-bold">That category doesn&apos;t exist</h1>
      <Link to="/toolkit" className="mt-4 inline-block text-sm font-semibold text-primary">
        Back to the toolkit
      </Link>
    </main>
  );
}
